// ─── Admin Authentication Context ────────────────────────────────────────────
// Wraps Supabase's onAuthStateChange so the whole admin tree knows
// immediately when the session appears or disappears.
// ─────────────────────────────────────────────────────────────────────────────

import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { loginAdmin, logoutAdmin } from "../services/adminAuthService";

const AdminAuthCtx = createContext();

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin]               = useState(null);
  const [isAuthenticated, setAuth]      = useState(false);
  const [loading, setLoading]           = useState(true);
  const [role, setRole]                 = useState(null);   // 'admin' | 'outreach' | 'viewer' | null
  const [roleLoading, setRoleLoading]   = useState(true);

  useEffect(() => {
    // onAuthStateChange fires an INITIAL_SESSION event immediately, use it as
    // the single source of truth so we're resilient to Strict Mode double-mounts
    // and any AbortError from getSession() lock contention.
    let loaded = false;

    if (!supabase) {
      setLoading(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setAdmin(session.user);
        setAuth(true);
      } else {
        setAdmin(null);
        setAuth(false);
        // TOKEN_REFRESHED failing or SIGNED_OUT mid-session means the JWT expired.
        // The AdminRoute will show the login form automatically once isAuthenticated
        // becomes false. Log it so we can diagnose refresh failures.
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('[AdminAuth] token refresh failed, session expired');
        }
      }
      if (!loaded) { loaded = true; setLoading(false); }
    });

    // Fallback: if onAuthStateChange never fires within 4s, unblock the UI
    const fallback = setTimeout(() => { if (!loaded) { loaded = true; setLoading(false); } }, 4000);

    return () => { subscription.unsubscribe(); clearTimeout(fallback); };
  }, []);

  // ── Resolve RBAC role ──────────────────────────────────────────────────────
  // Source of truth: team_members.role matched by email. Owner fallback: an
  // is_admin user with no team_members row resolves to 'admin' (full access), so
  // the owner can never be locked out. A non-admin with no row resolves to null
  // (deny by default). This is UI-level access control; data-layer enforcement
  // still relies on is_admin / RLS on the edge functions.
  useEffect(() => {
    let cancelled = false;
    async function resolveRole() {
      if (!admin?.email || !supabase) {
        if (!cancelled) { setRole(null); setRoleLoading(false); }
        return;
      }
      const ownerDefault = admin?.app_metadata?.is_admin === true ? 'admin' : null;
      setRoleLoading(true);
      try {
        const { data } = await supabase
          .from('team_members')
          .select('role, status')
          .eq('email', admin.email)
          .maybeSingle();
        if (cancelled) return;
        setRole(data?.role || ownerDefault);
      } catch {
        if (!cancelled) setRole(ownerDefault);
      } finally {
        if (!cancelled) setRoleLoading(false);
      }
    }
    resolveRole();
    return () => { cancelled = true; };
  }, [admin]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const result = await loginAdmin(email, password);
      return result;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await logoutAdmin();
    // onAuthStateChange fires and clears state automatically
  };

  // Derive admin role from app_metadata, never trust client-supplied values
  const isAdmin = admin?.app_metadata?.is_admin === true;

  return (
    <AdminAuthCtx.Provider value={{ admin, isAuthenticated, isAdmin, role, roleLoading, loading, login, logout }}>
      {children}
    </AdminAuthCtx.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthCtx);
  if (!context) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return context;
}
